import { useEffect, useState } from 'react'
import { Loader2, Search } from 'lucide-react'
import { supabase } from '@/lib/supabase/client'
import { ndtInputCls, ndtLabelCls, ndtHintCls } from '@/components/ndt/NdtFormPage'
import { STMIDBEquipmentService, isSTMIDBCompany } from '@/services/api/stmidbEquipmentService'
import { getSTMIDBNdtCategoryCode } from '@/utils/ndtEquipmentReport'
import { matchesCategoryFamily } from '@/utils/categoryFamily'

export interface NdtEquipmentHit {
  id: string
  name: string
  serial_number: string | null
  part_number: string | null
  /** Set for SLB hits, which have no equipment_v2 row to look the category up from. */
  category_code?: string | null
  /** 'stmidb' when the hit is SLB equipment read from the STMIDB project. */
  source?: 'stmidb'
}

const matchesCategoryFilter = (
  code: string | null,
  categoryCode: string | undefined,
  prefixes: string[],
): boolean => {
  const upper = (code || '').toUpperCase()
  if (categoryCode && upper !== categoryCode.toUpperCase()) return false
  if (prefixes.length === 0) return true
  return prefixes.some((prefix) => {
    const p = prefix.toUpperCase()
    if (/\d$/.test(p)) return matchesCategoryFamily(upper, p)
    if (!upper.startsWith(p)) return false
    return !(p === 'F' && upper.startsWith('FAL'))
  })
}

const searchSTMIDBEquipment = async (
  term: string,
  categoryCode: string | undefined,
  prefixes: string[],
): Promise<NdtEquipmentHit[]> => {
  const rows = await STMIDBEquipmentService.searchReportEquipment(term, 100)
  return rows
    .map((row) => ({
      row,
      code: getSTMIDBNdtCategoryCode(row.category, row.equipment_type),
    }))
    .filter(({ code }) => matchesCategoryFilter(code, categoryCode, prefixes))
    .slice(0, 20)
    .map(({ row, code }) => ({
      id: row.id,
      name: row.description || row.equipment_type || row.asset_number || 'Ukjent',
      serial_number: row.serial_number,
      part_number: row.part_number,
      category_code: code,
      source: 'stmidb' as const,
    }))
}

interface NdtEquipmentSearchProps {
  companyId: string
  categoryCode?: string
  categoryPrefix?: string
  categoryPrefixes?: string[]
  hint?: string
  emptyMessage?: string
  onSelect: (equipment: NdtEquipmentHit | null) => void
}

const NdtEquipmentSearch = ({
  companyId,
  categoryCode,
  categoryPrefix,
  categoryPrefixes,
  hint,
  emptyMessage = 'Ingen utstyr funnet',
  onSelect,
}: NdtEquipmentSearchProps) => {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<NdtEquipmentHit[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [showResults, setShowResults] = useState(false)

  const prefixes = categoryPrefixes?.length
    ? categoryPrefixes
    : categoryPrefix
      ? [categoryPrefix]
      : []

  useEffect(() => {
    const term = query.trim()
    if (!companyId || term.length < 1) {
      setResults([])
      setIsSearching(false)
      return
    }

    let cancelled = false
    const handle = window.setTimeout(async () => {
      setIsSearching(true)
      try {
        if (isSTMIDBCompany(companyId)) {
          const hits = await searchSTMIDBEquipment(term, categoryCode, prefixes)
          if (!cancelled) setResults(hits)
          return
        }

        const fetchForPrefix = async (prefix?: string) => {
          const needsCategory = !!categoryCode || !!prefix
          let request = needsCategory
            ? supabase
                .from('equipment_v2')
                .select('id, name, serial_number, part_number, categories_v2!inner(code)')
            : supabase
                .from('equipment_v2')
                .select('id, name, serial_number, part_number')

          request = request
            .eq('company_id', companyId)
            .ilike('serial_number', `%${term}%`)

          if (categoryCode) {
            request = request.eq('categories_v2.code', categoryCode)
          }
          if (prefix) {
            request = request.ilike('categories_v2.code', `${prefix}%`)
            if (prefix.toUpperCase() === 'F') {
              request = request.not('categories_v2.code', 'ilike', 'FAL%')
            }
          }

          const { data, error } = await request.order('serial_number').limit(20)
          if (error) return []
          return data ?? []
        }

        const rows = prefixes.length > 0
          ? (await Promise.all(prefixes.map((prefix) => fetchForPrefix(prefix)))).flat()
          : await fetchForPrefix()

        if (cancelled) return

        const unique = Array.from(new Map(rows.map((row) => [row.id, row])).values())
        setResults(unique.map((row) => ({
          id: row.id,
          name: row.name,
          serial_number: row.serial_number,
          part_number: row.part_number,
        })))
      } catch {
        if (!cancelled) setResults([])
      } finally {
        if (!cancelled) setIsSearching(false)
      }
    }, 300)

    return () => {
      cancelled = true
      window.clearTimeout(handle)
    }
  }, [query, companyId, categoryCode, prefixes.join('|')])

  const handleSelect = (equipment: NdtEquipmentHit) => {
    setQuery(equipment.serial_number || '')
    setShowResults(false)
    onSelect(equipment)
  }

  return (
    <div>
      <label className={ndtLabelCls}>Søk utstyr (SN)</label>
      <p className={ndtHintCls}>
        {companyId
          ? (hint || 'Søk etter serienummer hos valgt kunde')
          : 'Velg kunde først'}
      </p>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={query}
          disabled={!companyId}
          onChange={(e) => {
            onSelect(null)
            setQuery(e.target.value)
            setShowResults(true)
          }}
          onFocus={() => { if (results.length > 0) setShowResults(true) }}
          onBlur={() => window.setTimeout(() => setShowResults(false), 150)}
          placeholder={companyId ? 'Søk serienummer…' : 'Velg kunde først'}
          className={`${ndtInputCls} pl-9`}
        />
        {isSearching && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />}
        {showResults && companyId && query.trim() && (
          <div className="absolute z-20 mt-1 w-full max-h-56 overflow-auto border border-slate-200 bg-white rounded-md shadow-lg">
            {results.length === 0 && !isSearching ? (
              <div className="px-3 py-2 text-sm text-slate-400">{emptyMessage}</div>
            ) : results.map((eq) => (
              <button
                key={eq.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(eq)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
              >
                <div className="font-medium text-slate-800">{eq.serial_number || 'Uten SN'}</div>
                <div className="text-xs text-slate-400">
                  {eq.name}{eq.part_number ? ` · PN ${eq.part_number}` : ''}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default NdtEquipmentSearch
